import type { PacienteResponseEspecie, PacienteResponseSexo } from '@/api/generated/model';

/**
 * Rótulos das espécies e do sexo.
 *
 * `Record` fechado sobre o tipo gerado: espécie nova no backend vira erro de
 * compilação aqui, e não `CAPRINO` cru aparecendo na tabela.
 */
export const ESPECIES: Record<PacienteResponseEspecie, string> = {
  CANINO: 'Canino',
  FELINO: 'Felino',
  EQUINO: 'Equino',
  BOVINO: 'Bovino',
  SUINO: 'Suíno',
  CAPRINO: 'Caprino',
  OVINO: 'Ovino',
  AVE: 'Ave',
  REPTIL: 'Réptil',
  ROEDOR: 'Roedor',
  OUTRO: 'Outro',
};

export const SEXOS: Record<PacienteResponseSexo, string> = {
  MACHO: 'Macho',
  FEMEA: 'Fêmea',
  INDEFINIDO: 'Não informado',
};

/**
 * Mesma paleta de `PacienteCard.tsx` — macho azul, fêmea na cor de marca
 * (rosa), indefinido neutro. `bordaSelecionada` é a versão saturada (sem
 * opacidade) pra estados de seleção fora do card original (ex.: o picker de
 * paciente do wizard de discussão).
 */
export const CORES_SEXO: Record<
  PacienteResponseSexo,
  { borda: string; bordaSelecionada: string; fundo: string; texto: string; botao: string }
> = {
  FEMEA: {
    borda: 'border-brand/25 hover:border-brand/50',
    bordaSelecionada: 'border-brand',
    fundo: 'bg-brand/10',
    texto: 'text-brand',
    botao: 'bg-brand hover:bg-brand/80 text-brand-foreground',
  },
  MACHO: {
    borda: 'border-blue-600/25 hover:border-blue-600/50',
    bordaSelecionada: 'border-blue-600',
    fundo: 'bg-blue-600/10',
    texto: 'text-blue-600',
    botao: 'bg-blue-600 hover:bg-blue-600/80 text-white',
  },
  INDEFINIDO: {
    borda: 'border-border hover:border-brand/30',
    bordaSelecionada: 'border-brand',
    fundo: 'bg-muted',
    texto: 'text-muted-foreground',
    botao: 'bg-brand hover:bg-brand/80 text-brand-foreground',
  },
};

/**
 * "3 anos e 2 meses" · "7 meses" · "—".
 *
 * O servidor manda idade em MESES, e é ele quem calcula: a tela que faz a
 * própria conta a partir do nascimento diverge do laudo na virada do mês.
 */
export function formatarIdade(idadeMeses: number | null): string {
  if (idadeMeses === null) return '—';
  if (idadeMeses < 1) return 'menos de 1 mês';

  const anos = Math.floor(idadeMeses / 12);
  const meses = idadeMeses % 12;

  const partes: string[] = [];
  if (anos > 0) partes.push(anos === 1 ? '1 ano' : `${anos} anos`);
  if (meses > 0) partes.push(meses === 1 ? '1 mês' : `${meses} meses`);

  return partes.join(' e ');
}
